import { useAgenda } from '../../context/AgendaContext';
import { useTimer } from '../../context/TimerContext';
import { Card, CardHeader, CardBody } from '../UI/Card';
import { CountdownTimer } from './CountdownTimer';
import { PowerBreakButton } from './PowerBreakButton';
import { PreEventCountdown } from './PreEventCountdown';
import { TeamDisplay } from '../Teams/TeamDisplay';
import { CycleTimer } from './CycleTimer';
import { Button } from '../UI/Button';
import { usePreEventCountdown } from '../../hooks/usePreEventCountdown';
import { useTeamRandomization } from '../../hooks/useTeamRandomization';
import styles from './LiveAgendaTracker.module.css';

/**
 * LiveAgendaTracker component displays the current agenda item with countdown timer
 * and Power Break functionality
 * 
 * Requirements:
 * - Consume AgendaContext and TimerContext
 * - Display current agenda item title and additional info
 * - Render CountdownTimer for agenda or Power Break
 * - Render PowerBreakButton when agenda item is active
 * - Show default message when no item is active
 * - Apply Card component as base container
 * - Use fire accent (--fire-2) for current item indicator
 * - Apply sticky positioning on mobile (position: sticky, top: 0)
 * - Use elevated background (--bg-elev-1) for prominence
 * - Display pre-event countdown before event starts
 * - Hide Power Break button during pre-event countdown
 * - Display team assignments when current item requires teams
 * - Show "Generate Teams" button if teams not yet generated
 * 
 * Validates: Requirements 2.1, 2.2, 2.3, 2.4, 2.5, 4.1, 14.1, 14.5, 14.6, 14.8, 15.9, 15.11
 */
export function LiveAgendaTracker() {
  const { currentItem } = useAgenda();
  const {
    timeRemaining,
    isPowerBreakActive,
    powerBreakTimeRemaining,
    startPowerBreak,
    cancelPowerBreak,
  } = useTimer();
  
  const { isPreEvent, days, hours, minutes, seconds } = usePreEventCountdown();
  const { getTeamsForItem, generateTeamsForItem, isLoading: isTeamsLoading } = useTeamRandomization();

  // Get teams for current item if it requires teams
  const currentTeams = currentItem?.requiresTeams && currentItem.id 
    ? getTeamsForItem(currentItem.id) 
    : undefined;

  const handleGenerateTeams = async () => {
    if (!currentItem?.id) return;
    await generateTeamsForItem(currentItem.id);
  };

  const handleCycleStart = async () => {
    // Regenerate teams when a new cycle starts
    if (currentItem?.requiresTeams && currentItem.id) {
      await generateTeamsForItem(currentItem.id);
    }
  };

  // Show pre-event countdown if event hasn't started
  if (isPreEvent) {
    return (
      <div className={styles.container}>
        <PreEventCountdown
          days={days}
          hours={hours}
          minutes={minutes}
          seconds={seconds}
        />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        {currentItem ? (
          <>
            <CardHeader>
              <div className={styles.header}>
                <div className={styles.indicator} />
                <h2 className={styles.title}>{currentItem.title}</h2>
              </div>
            </CardHeader>
            <CardBody>
              {currentItem.additionalInfo && (
                <p className={styles.info}>{currentItem.additionalInfo}</p>
              )}
              
              {isPowerBreakActive ? (
                <CountdownTimer
                  timeRemaining={powerBreakTimeRemaining}
                  label="Power Break"
                  variant="power-break"
                />
              ) : (
                <CountdownTimer
                  timeRemaining={timeRemaining}
                  label="Time Remaining"
                  variant="agenda"
                />
              )}

              <div className={styles.actions}>
                <PowerBreakButton
                  onStart={startPowerBreak}
                  isActive={isPowerBreakActive}
                  onCancel={cancelPowerBreak}
                />
              </div>

              {/* Team Display Section */}
              {currentItem.requiresTeams && (
                <div className={styles.teamsSection}>
                  {currentTeams ? (
                    <TeamDisplay
                      teams={currentTeams}
                      onRegenerateTeams={handleGenerateTeams}
                      isLoading={isTeamsLoading}
                    />
                  ) : (
                    <div className={styles.generateTeamsPrompt}>
                      <p className={styles.promptMessage}>
                        This activity requires team assignments.
                      </p>
                      <Button
                        variant="primary"
                        onClick={handleGenerateTeams}
                        disabled={isTeamsLoading}
                      >
                        {isTeamsLoading ? 'Generating...' : 'Generate Teams'}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Cycle Timer Section */}
              {currentItem.requiresCycle && (
                <CycleTimer onCycleStart={handleCycleStart} />
              )}
            </CardBody>
          </>
        ) : (
          <CardBody>
            <p className={styles.defaultMessage}>
              No active agenda item at this time. Check the full agenda below for upcoming events.
            </p>
          </CardBody>
        )}
      </Card>
    </div>
  );
}
